import { Component } from '@theme/component';

/** Theme-owned favourites and existing room photographs; no account or upload service. */
class CloseStudyTools extends Component {
  #abort;
  #observer;
  #frame;
  #timer;
  #items = [];
  #storageKey = 'rg.saved-photographs.v1';
  #opener;
  #sharedItems = null;
  #sharedRequest = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    this.#read();
    document.documentElement.classList.add('rg-cs-ready');
    this.#sync();
    const signal = this.#abort.signal;
    window.addEventListener('hashchange', () => this.#loadShared(), { signal });
    this.#loadShared();
    window.addEventListener('storage', (event) => {
      if (event.key === this.#storageKey) { this.#read(); this.#sync(); this.#renderSaved(); }
    }, { signal });
    this.querySelectorAll('#rg-close-saved, #rg-close-room').forEach((dialog) => {
      dialog.addEventListener('dialog:close', () => {
        if (this.#opener?.isConnected) this.#opener.focus({ preventScroll: true });
      }, { signal });
    });
    this.#observer = new MutationObserver(() => {
      if (this.#frame) return;
      this.#frame = requestAnimationFrame(() => { this.#frame = null; this.#sync(); });
    });
    this.#observer.observe(document.body, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#timer);
  }

  #safeProductURL(value) {
    try { const url = new URL(value, location.origin); return url.origin === location.origin && /\/products\/[^/]+$/.test(url.pathname) ? url.pathname : ''; }
    catch { return ''; }
  }

  #safeImage(value) {
    try { const url = new URL(value, location.origin); return url.protocol === 'https:' && (url.hostname === location.hostname || url.hostname === 'cdn.shopify.com') ? url.href : ''; }
    catch { return ''; }
  }

  #read() {
    try {
      const data = JSON.parse(localStorage.getItem(this.#storageKey) || '[]');
      this.#items = Array.isArray(data) ? data.filter((item) => item && /^\d+$/.test(item.id) && this.#safeProductURL(item.url)).slice(0, 100).map((item) => ({ id: String(item.id), title: String(item.title || '').slice(0, 200), vendor: String(item.vendor || '').slice(0, 200), url: this.#safeProductURL(item.url), image: this.#safeImage(item.image) })) : [];
    } catch { this.#items = []; }
  }

  #sync() {
    const saved = new Set(this.#items.map((item) => item.id));
    document.querySelectorAll('[data-rg-save]').forEach((button) => {
      const active = saved.has(button.dataset.rgSave);
      const state = String(active);
      const label = (active ? this.dataset.removeLabel : this.dataset.saveLabel) + ': ' + button.dataset.rgTitle;
      if (button.getAttribute('aria-pressed') !== state) button.setAttribute('aria-pressed', state);
      if (button.getAttribute('aria-label') !== label) button.setAttribute('aria-label', label);
    });
    document.querySelectorAll('[data-rg-saved-count]').forEach((count) => {
      const text = String(this.#items.length);
      if (count.textContent !== text) count.textContent = text;
      count.hidden = !this.#items.length;
    });
  }

  #announce(text) {
    const status = this.refs.status;
    status.textContent = text;
    status.hidden = false;
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => { status.hidden = true; }, 3000);
  }

  saveProduct(event) {
    const button = event.target.closest('[data-rg-save]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    const previous = [...this.#items];
    const id = button.dataset.rgSave;
    const exists = this.#items.some((item) => item.id === id);
    if (exists) this.#items = this.#items.filter((item) => item.id !== id);
    else {
      const url = this.#safeProductURL(button.dataset.rgUrl);
      if (!url) return;
      this.#items.unshift({ id, title: button.dataset.rgTitle, vendor: button.dataset.rgVendor || '', image: this.#safeImage(button.dataset.rgImage), url });
      this.#items = this.#items.slice(0, 100);
    }
    try { localStorage.setItem(this.#storageKey, JSON.stringify(this.#items)); }
    catch { this.#items = previous; this.#announce(this.dataset.storageError); return; }
    this.querySelector('[ref="wishlistStatus"]').textContent = '';
    this.#sync();
    if (button.closest('.rg-cs-saved-grid')) {
      const index = Array.from(this.querySelector('[ref="savedList"]').querySelectorAll('button')).indexOf(button);
      this.#renderSaved();
      const remaining = this.querySelector('[ref="savedList"]').querySelectorAll('button');
      (remaining[Math.min(index, remaining.length - 1)] || this.querySelector('#rg-close-saved .rg-cs-close')).focus();
    }
    this.#announce(exists ? this.dataset.removed : this.dataset.added);
  }

  #renderSaved() {
    const list = this.querySelector('[ref="savedList"]');
    if (!list) return;
    const items = this.#sharedItems ?? this.#items;
    const shared = this.#sharedItems !== null;
    const shareInput = this.querySelector('#rg-wishlist-link');
    if (shareInput) {
      shareInput.value = '';
      shareInput.parentElement.hidden = true;
    }
    this.querySelector('[ref="wishlistStatus"]').textContent = '';
    list.replaceChildren();
    this.querySelector('[ref="savedEmpty"]').hidden = items.length > 0;
    this.querySelector('#rg-cs-saved-title').textContent = shared ? 'Shared wishlist' : 'Wishlist';
    this.querySelector('.rg-cs-wishlist-share').disabled = !items.length;
    this.querySelector('.rg-cs-wishlist-keep').hidden = !shared || !items.length;
    this.querySelector('[ref="wishlistNote"]').textContent = shared ? 'A collection of favourites shared with you. Save it to keep these artworks on this browser.' : 'Your favourites are saved on this browser. Share your wishlist to open it on another device.';
    for (const item of items) {
      const li = document.createElement('li');
      const imageLink = document.createElement('a'); imageLink.href = item.url; imageLink.className = 'rg-cs-wishlist-image';
      imageLink.setAttribute('aria-label', item.title);
      if (item.image) {
        const img = document.createElement('img'); img.src = item.image; img.alt = ''; img.loading = 'lazy'; img.width = 300; img.height = 300; imageLink.append(img);
      }
      const content = document.createElement('div'); content.className = 'rg-cs-wishlist-copy';
      const title = document.createElement('h3'); title.textContent = item.title;
      const artist = document.createElement('p'); artist.textContent = this.dataset.artistLabel;
      const actions = document.createElement('div'); actions.className = 'rg-cs-wishlist-item-actions';
      const link = document.createElement('a'); link.href = item.url; link.className = 'rg-cs-wishlist-view'; link.textContent = /print/i.test(item.title) ? 'View Print' : 'View Product';
      actions.append(link);
      if (!shared) {
        const remove = document.createElement('button'); remove.type = 'button'; remove.className = 'button-unstyled rg-cs-saved-remove';
        remove.textContent = 'Remove'; remove.setAttribute('on:click', '#rg-close-tools/saveProduct');
        remove.dataset.rgSave = item.id; remove.dataset.rgTitle = item.title; remove.setAttribute('aria-label', this.dataset.removeLabel + ': ' + item.title);
        actions.append(remove);
      }
      content.append(title, artist, actions); li.append(imageLink, content); list.append(li);
    }
  }

  async shareSaved() {
    const items = this.#sharedItems ?? this.#items;
    if (!items.length) return;
    const url = new URL(this.dataset.rootUrl || '/', location.origin);
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    else if (document.querySelector('#PBarNextFrame') && window.Shopify?.theme?.id && window.Shopify.theme.role !== 'main') url.searchParams.set('preview_theme_id', window.Shopify.theme.id);
    url.hash = 'rg-wishlist=' + items.map(item => item.url.split('/').pop()).join(',');
    const input = this.querySelector('#rg-wishlist-link'); input.value = url.href;
    const status = this.querySelector('[ref="wishlistStatus"]');
    input.parentElement.hidden = false;
    try {
      await navigator.clipboard.writeText(url.href);
      if (input.value === url.href) status.textContent = 'Wishlist link copied.';
    } catch {
      if (input.value !== url.href) return;
      input.focus(); input.select(); status.textContent = 'Copy this link to share your wishlist.';
    }
  }

  async #loadShared() {
    if (!location.hash.startsWith('#rg-wishlist=')) return;
    const value = location.hash.slice(13);
    if (!value || value.length > 20000) return;
    const handles = [...new Set(value.split(','))].filter(handle => /^[a-z0-9][a-z0-9-]{0,199}$/.test(handle)).slice(0,100);
    if (!handles.length || !this.querySelector('#rg-close-saved')) return;
    const request = ++this.#sharedRequest;
    this.#sharedItems = []; this.#renderSaved();
    this.querySelector('[ref="savedEmpty"]').hidden = true;
    const status = this.querySelector('[ref="wishlistStatus"]'); status.textContent = 'Loading shared wishlist…';
    this.querySelector('#rg-close-saved').showDialog();
    const items = new Array(handles.length); let cursor = 0;
    const worker = async () => {
      while (cursor < handles.length && request === this.#sharedRequest) {
        const index = cursor++; const url = (this.dataset.rootUrl || '/') + 'products/' + handles[index];
        try {
          const response = await fetch(url + '.js', { signal: this.#abort.signal });
          if (!response.ok) continue;
          const product = await response.json();
          if (!product.id || !product.title) continue;
          items[index] = { id:String(product.id), title:product.title, vendor:product.vendor || '', image:this.#safeImage(product.featured_image), url:this.#safeProductURL(url) };
        } catch { /* Unavailable artworks are omitted from the shared view. */ }
      }
    };
    await Promise.all(Array.from({length:Math.min(4,handles.length)}, worker));
    if (request !== this.#sharedRequest || !this.isConnected) return;
    this.#sharedItems = items.filter(item => item?.url); this.#renderSaved();
    status.textContent = this.#sharedItems.length < handles.length ? 'Some artworks are no longer available.' : '';
  }

  keepShared() {
    if (!this.#sharedItems?.length) return;
    const items = [...new Map([...this.#sharedItems, ...this.#items].map(item => [item.id,item])).values()].slice(0,100);
    try { localStorage.setItem(this.#storageKey, JSON.stringify(items)); }
    catch { this.querySelector('[ref="wishlistStatus"]').textContent = this.dataset.storageError; return; }
    this.#items = items; this.#sharedItems = null;
    history.replaceState(null, '', location.pathname + location.search);
    this.#sync(); this.#renderSaved();
    this.querySelector('[ref="wishlistStatus"]').textContent = 'Wishlist saved on this browser.';
    this.querySelector('.rg-cs-wishlist-share').focus();
  }

  async openSaved(event) {
    const trigger = event.target.closest('button');
    const drawer = trigger?.closest('header-drawer');
    this.#opener = drawer?.querySelector('summary') || trigger;
    if (drawer?.isOpen) {
      const details = drawer.querySelector('.menu-drawer-container');
      await new Promise(resolve => {
        const observer = new MutationObserver(() => { if (!details.open) { observer.disconnect(); resolve(); } });
        observer.observe(details, { attributes: true, attributeFilter: ['open'] });
        drawer.close();
      });
    }
    this.#sharedRequest++; this.#sharedItems = null;
    this.querySelector('[ref="wishlistStatus"]').textContent = '';
    this.querySelector('#rg-wishlist-link').parentElement.hidden = true;
    this.#read(); this.#sync(); this.#renderSaved();
    this.querySelector('#rg-close-saved')?.showDialog();
  }

  openRoom(event) {
    const button = event.target.closest('[data-rg-room]');
    if (!button) return;
    const url = this.#safeProductURL(button.dataset.rgUrl);
    const image = this.#safeImage(button.dataset.rgRoom);
    if (!url || !image) return;
    event.preventDefault(); event.stopPropagation();
    this.#opener = button;
    this.querySelector('[ref="roomTitle"]').textContent = button.dataset.rgTitle;
    const img = document.createElement('img'); img.src = image; img.alt = button.dataset.rgTitle; img.width = 1800; img.height = 1200;
    this.querySelector('[ref="roomImage"]').replaceChildren(img); this.querySelector('[ref="roomLink"]').href = url;
    this.querySelector('#rg-close-room')?.showDialog();
  }
}
if (!customElements.get('rg-close-tools')) customElements.define('rg-close-tools', CloseStudyTools);

/** Live search of this collection, including pages not yet loaded by the native grid. */
class CloseCollectionSearch extends Component {
  #abort; #fetch; #timer; #viewObserver; #query = ''; #cache = new Map(); #failed = false;
  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const signal = this.#abort.signal;
    this.#query = new URL(location.href).searchParams.get('q') || '';
    this.addEventListener('input', (event) => {
      if (!event.target.matches('.rg-ed-collection-search input[type="search"]')) return;
      this.#query = event.target.value;
      clearTimeout(this.#timer);
      this.#timer = setTimeout(() => this.#search(), 220);
    }, { signal });
    this.addEventListener('submit', (event) => {
      if (!event.target.matches('.rg-ed-collection-search') || this.#failed) return;
      event.preventDefault(); clearTimeout(this.#timer); this.#search();
    }, { signal });
    this.addEventListener('click', (event) => {
      if (!event.target.closest('.rg-cs-inline-clear')) return;
      this.#query = ''; this.#input.value = ''; this.#search(); this.#input.focus();
    }, { signal });
    document.addEventListener('rg:collection-updated', (event) => {
      if (event.detail.sectionId !== this.querySelector('results-list')?.getAttribute('section-id')) return;
      this.#observeGrid();
      if (this.#query) { this.#input.value = this.#query; this.#search(); }
    }, { signal });
    this.#observeGrid();
    if (this.#query) { this.#input.value = this.#query; this.#search(); }
  }
  disconnectedCallback() {
    super.disconnectedCallback(); this.#abort?.abort(); this.#fetch?.abort(); this.#viewObserver?.disconnect(); clearTimeout(this.#timer);
  }
  get #input() { return this.querySelector('.rg-ed-collection-search input[type="search"]'); }
  #observeGrid() {
    this.#viewObserver?.disconnect();
    const grid = this.querySelector('[ref="grid"]');
    if (!grid) return;
    const sync = () => this.querySelector('.rg-cs-inline-grid')?.setAttribute('data-view', grid.getAttribute('product-grid-view') || 'default');
    sync(); this.#viewObserver = new MutationObserver(sync); this.#viewObserver.observe(grid, { attributes: true, attributeFilter: ['product-grid-view'] });
  }
  #normalize(value) { return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase(); }
  async #index(url, signal) {
    const key = url.toString();
    if (this.#cache.has(key)) return this.#cache.get(key);
    const read = async (page) => {
      const requestURL = new URL(url); requestURL.searchParams.set('section_id', 'rg-collection-index'); requestURL.searchParams.set('page', String(page));
      const response = await fetch(requestURL, { signal });
      if (!response.ok) throw new Error('Collection search request failed');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const index = doc.querySelector('[data-rg-search-index]');
      if (!index) throw new Error('Collection search response unavailable');
      return index;
    };
    const first = await read(1); const pages = Number(first.dataset.pages) || 1;
    const sources = [first];
    for (let page = 2; page <= pages; page++) sources.push(await read(page));
    const items = sources.flatMap(source => [...source.querySelectorAll('template[data-rg-index-card]')].map(template => ({ keywords: this.#normalize(template.dataset.keywords || ''), template })));
    if (!signal.aborted) this.#cache.set(key, items);
    return items;
  }
  async #search() {
    this.#fetch?.abort(); this.#fetch = new AbortController(); const signal = this.#fetch.signal;
    const query = this.#query.trim();
    const panel = this.querySelector('.rg-cs-inline-results'); const grid = panel?.querySelector('ul'); const status = panel?.querySelector('[role="status"]');
    if (!panel || !grid || !status) return;
    const url = new URL(location.href); url.searchParams.delete('page');
    if (query) url.searchParams.set('q', query); else url.searchParams.delete('q');
    history.replaceState(history.state, '', url);
    this.querySelector('.rg-cs-inline-clear').hidden = !query;
    this.toggleAttribute('data-search-active', !!query); panel.hidden = !query;
    const count = document.querySelector('.rg-cs-collection-count');
    if (!query) { grid.replaceChildren(); status.textContent = ''; this.#failed = false; this.removeAttribute('aria-busy'); if (count) count.textContent = this.dataset.total; return; }
    this.setAttribute('aria-busy', 'true'); status.textContent = this.dataset.loading; this.#failed = false;
    url.searchParams.delete('q'); url.searchParams.delete('preview_theme_id');
    try {
      const items = await this.#index(url, signal);
      if (signal.aborted || !this.isConnected) return;
      const terms = this.#normalize(query).split(/\s+/).filter(Boolean);
      const matches = items.filter(item => terms.every(term => item.keywords.includes(term)));
      const fragment = document.createDocumentFragment();
      matches.forEach(item => fragment.append(item.template.content.cloneNode(true)));
      grid.replaceChildren(fragment);
      status.textContent = matches.length ? matches.length + ' ' + (matches.length === 1 ? this.dataset.countLabel.replace(/s$/, '') : this.dataset.countLabel) : this.dataset.empty;
      if (count) count.textContent = String(matches.length);
      this.#observeGrid();
    } catch (error) {
      if (signal.aborted) return;
      this.#failed = true; grid.replaceChildren(); status.textContent = this.dataset.error;
    } finally { if (!signal.aborted) this.removeAttribute('aria-busy'); }
  }
}
if (!customElements.get('rg-collection-search')) customElements.define('rg-collection-search', CloseCollectionSearch);

/** Genuine store-wide reviews from the existing public Judge.me integration. */
class CloseReviewSummary extends Component {
  static requests = new Map();
  #reviews = []; #shown = 0;
  connectedCallback() { super.connectedCallback(); this.#load(); }
  async #load() {
    const key = this.dataset.shopDomain + ':' + this.dataset.apiToken;
    if (!this.dataset.apiToken) return;
    if (!CloseReviewSummary.requests.has(key)) CloseReviewSummary.requests.set(key, this.#fetchReviews());
    try {
      this.#reviews = await CloseReviewSummary.requests.get(key);
      if (!this.isConnected || !this.#reviews.length) return;
      const average = this.#reviews.reduce((sum, review) => sum + Number(review.rating), 0) / this.#reviews.length;
      const button = this.querySelector('.rg-cs-review-trigger');
      this.querySelector('[data-review-count]').textContent = this.#reviews.length + ' ' + this.dataset.label;
      this.querySelector('[data-review-total]').textContent = String(this.#reviews.length);
      button.style.setProperty('--rating-fill', (average / 5 * 100) + '%');
      button.setAttribute('aria-label', average.toFixed(2) + ' / 5 · ' + this.#reviews.length + ' ' + this.dataset.label + '. ' + button.title);
      button.hidden = false; this.showMore();
    } catch { CloseReviewSummary.requests.delete(key); }
  }
  async #fetchReviews() {
    const result = []; const seen = new Set();
    for (let page = 1; page <= 100; page++) {
      const url = new URL('https://judge.me/api/v1/widgets/all_reviews_page');
      Object.entries({api_token:this.dataset.apiToken,shop_domain:this.dataset.shopDomain,per_page:'25',page:String(page),json_request:'true'}).forEach(([name,value])=>url.searchParams.set(name,value));
      const response = await fetch(url);
      if (!response.ok) throw new Error('Reviews unavailable');
      const data = await response.json(); if (!Array.isArray(data.reviews)) throw new Error('Invalid review response');
      let added = 0;
      for (const review of data.reviews) {
        const id = review.uuid || [review.reviewer_name,review.body,review.created_at].join('|');
        if (seen.has(id)) continue; seen.add(id); added++;
        if (Number(review.rating) >= 1 && Number(review.rating) <= 5) result.push(review);
      }
      if (data.reviews.length < 25 || !added) return result;
    }
    throw new Error('Incomplete review response');
  }
  #node(tag, className, text) { const node=document.createElement(tag);if(className)node.className=className;if(text)node.textContent=text;return node; }
  showMore() {
    const list=this.querySelector('.rg-cs-review-list'); if(!list)return;
    const end=Math.min(this.#shown+12,this.#reviews.length);
    for(const review of this.#reviews.slice(this.#shown,end)) {
      const row=this.#node('article','rg-cs-review');row.setAttribute('role','listitem');
      const top=this.#node('div','rg-cs-review-top');
      const name=this.#node('span','rg-cs-review-name',review.reviewer_name || 'Customer');
      const stars=this.#node('span','rg-cs-review-stars','★'.repeat(Math.round(Number(review.rating)))+'☆'.repeat(5-Math.round(Number(review.rating))));stars.setAttribute('aria-label',review.rating+' / 5');
      top.append(name,stars);
      const date=new Date(review.created_at);if(!Number.isNaN(date.valueOf())){const time=this.#node('time','',new Intl.DateTimeFormat(document.documentElement.lang||'en',{dateStyle:'medium'}).format(date));time.dateTime=date.toISOString();top.append(time);}row.append(top);
      if(review.verified_buyer===true || review.verified_buyer==='true')row.append(this.#node('p','rg-cs-review-verified',this.dataset.verified+' ✓'));
      if(review.product_title){const product=this.#node('div','rg-cs-review-product');const copy=this.#node('div');copy.append(this.#node('span','',this.dataset.reviewing));let url;try{const value=new URL(review.product_url,location.origin);if(value.origin===location.origin&&/\/products\/[^/]+$/.test(value.pathname))url=value;}catch{}
        const title=this.#node(url?'a':'span','rg-cs-review-product-title',review.product_title);if(url)title.href=url.pathname;copy.append(title);product.append(copy);row.append(product);
        if(url)this.#productImage(url,product,review.product_title);
      }
      if(review.title)row.append(this.#node('h3','',review.title));
      row.append(this.#node('p','rg-cs-review-body',review.body||''));list.append(row);
    }
    this.#shown=end;this.querySelector('.rg-cs-review-more').hidden=end>=this.#reviews.length;
  }
  async #productImage(url, container, title) {try {const response=await fetch(url.pathname+'.js');if(!response.ok)return;const product=await response.json();const value=product.featured_image;if(!value)return;const imageURL=new URL(value,location.origin);if(imageURL.protocol!=='https:'||![location.hostname,'cdn.shopify.com'].includes(imageURL.hostname))return;const image=this.#node('img');image.src=imageURL.href;image.alt=title;image.width=80;image.height=96;image.loading='lazy';container.prepend(image);}catch{} }
}
if(!customElements.get('rg-review-summary'))customElements.define('rg-review-summary',CloseReviewSummary);

/** Optional display preferences stored on this browser; native semantics remain intact. */
class CloseAccessibility extends Component {
  #settings={text:1,contrast:false,links:false,motion:false}; #observer; #frame; #abort; #paused=new Set();
  connectedCallback(){super.connectedCallback();this.#abort=new AbortController();try{const data=JSON.parse(localStorage.getItem('rg.accessibility.v1')||'{}');this.#settings={text:[1,1.25,1.5,2].includes(Number(data.text))?Number(data.text):1,contrast:data.contrast===true,links:data.links===true,motion:data.motion===true};}catch{}
    this.#apply();this.#observer=new MutationObserver((records)=>{if(!records.some(record=>record.addedNodes.length||record.removedNodes.length))return;cancelAnimationFrame(this.#frame);this.#frame=requestAnimationFrame(()=>this.#applyText());});this.#observer.observe(document.body,{childList:true,subtree:true});
    document.addEventListener('play',event=>{if(this.#settings.motion&&event.target instanceof HTMLVideoElement){this.#paused.add(event.target);event.target.pause();}},{capture:true,signal:this.#abort.signal});
  }
  disconnectedCallback(){super.disconnectedCallback();this.#observer?.disconnect();this.#abort?.abort();cancelAnimationFrame(this.#frame);}
  change(event){const control=event.target;if(control.name==='text')this.#settings.text=Number(control.value);else if(['contrast','links','motion'].includes(control.name))this.#settings[control.name]=control.checked;this.#save();this.#apply();this.querySelector('[role="status"]').textContent=this.dataset.updated;}
  reset(){this.#settings={text:1,contrast:false,links:false,motion:false};this.#save();this.#apply();this.querySelector('[role="status"]').textContent=this.dataset.updated;}
  #save(){try{localStorage.setItem('rg.accessibility.v1',JSON.stringify(this.#settings));}catch{}}
  #apply(){const body=document.body;body.classList.toggle('rg-a11y-contrast',this.#settings.contrast);body.classList.toggle('rg-a11y-links',this.#settings.links);body.classList.toggle('rg-a11y-motion',this.#settings.motion);body.classList.toggle('rg-a11y-large',this.#settings.text>=1.5);body.style.setProperty('--rg-text-scale',String(this.#settings.text));this.querySelector('select').value=String(this.#settings.text);for(const input of this.querySelectorAll('input[type="checkbox"]'))input.checked=this.#settings[input.name];
    for(const video of document.querySelectorAll('video')){if(this.#settings.motion&&!video.paused){this.#paused.add(video);video.pause();}}
    if(!this.#settings.motion){for(const video of this.#paused){if(video.isConnected&&!video.closest('rg-hero-media, rg-collection-media, rg-menu-video'))video.play().catch(()=>{});}this.#paused.clear();}
    this.#applyText();
  }
  #applyText(){document.body.classList.add('rg-a11y-measuring');const previous=[...document.querySelectorAll('[data-rg-text-adjust]')];previous.forEach(el=>el.removeAttribute('data-rg-text-adjust'));if(this.#settings.text===1&&!this.#settings.contrast){document.body.classList.remove('rg-a11y-measuring');return;}
    const candidates=[...document.querySelectorAll('h1,h2,h3,h4,h5,h6,p,a,button,input,textarea,select,summary,legend,label,span,li,th,td,strong,em')].filter(el=>!el.closest('rg-accessibility,svg,.visually-hidden,.svg-wrapper')&&el.getBoundingClientRect().width>0&&([...el.childNodes].some(node=>node.nodeType===3&&node.textContent.trim())||el.matches('input,textarea,select')));
    const sizes=candidates.map(el=>[el,getComputedStyle(el).fontSize]);for(const [el,size]of sizes){el.style.setProperty('--rg-original-text',size);el.setAttribute('data-rg-text-adjust','');}document.body.classList.remove('rg-a11y-measuring');
  }
}
if(!customElements.get('rg-accessibility'))customElements.define('rg-accessibility',CloseAccessibility);

/** Load a film only when its responsive surface can actually play. */
function loadCloseVideo(video) {
  const sources = video.querySelectorAll('source[data-rg-src]');
  if (!sources.length) return;
  for (const source of sources) {
    source.src = source.dataset.rgSrc;
    source.removeAttribute('data-rg-src');
  }
  video.load();
}

class CloseHeroMedia extends HTMLElement {
  #abort; #observer; #bodyObserver; #visible = false;
  #motion = matchMedia('(prefers-reduced-motion: reduce)');
  #mobile = matchMedia('(max-width:749px)');
  connectedCallback() {
    if (!this.querySelector('video')) return;
    this.#abort = new AbortController();
    const signal = this.#abort.signal;
    this.#observer = new IntersectionObserver(entries => {
      this.#visible = entries[0].isIntersecting;
      this.#sync();
    }, { threshold: 0 });
    this.#observer.observe(this);
    this.#bodyObserver = new MutationObserver(() => this.#sync());
    this.#bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    this.#motion.addEventListener('change', () => this.#sync(), { signal });
    this.#mobile.addEventListener('change', () => this.#sync(), { signal });
    document.addEventListener('visibilitychange', () => this.#sync(), { signal });
    for (const video of this.querySelectorAll('video')) {
      video.addEventListener('play', () => { if (!this.#canPlay(video)) video.pause(); }, { signal });
    }
  }
  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    this.#bodyObserver?.disconnect();
    for (const video of this.querySelectorAll('video')) video.pause();
  }
  #canPlay(video) {
    const box = video.getBoundingClientRect();
    return this.#visible && !document.hidden && !this.#motion.matches &&
      !document.body.classList.contains('rg-a11y-motion') && box.width > 0 && box.height > 0;
  }
  #sync() {
    for (const video of this.querySelectorAll('video')) {
      if (!this.#canPlay(video)) video.pause();
      else { loadCloseVideo(video); video.play().catch(() => {}); }
    }
  }
}
if (!customElements.get('rg-hero-media')) customElements.define('rg-hero-media', CloseHeroMedia);

class CloseMenuVideo extends Component {
  #observer; #bodyObserver; #abort; #manualPause=false; #motion=matchMedia('(prefers-reduced-motion: reduce)'); #desktop=matchMedia('(min-width:750px)');
  connectedCallback(){super.connectedCallback();this.#abort=new AbortController();const signal=this.#abort.signal;this.#observer=new MutationObserver(()=>this.#sync());this.#observer.observe(this.closest('header-drawer'),{attributes:true,subtree:true,attributeFilter:['open']});this.#bodyObserver=new MutationObserver(()=>this.#sync());this.#bodyObserver.observe(document.body,{attributes:true,attributeFilter:['class']});this.#motion.addEventListener('change',()=>this.#sync(),{signal});this.#desktop.addEventListener('change',()=>this.#sync(),{signal});document.addEventListener('visibilitychange',()=>this.#sync(),{signal});const v=this.querySelector('video');for(const event of ['play','pause'])v.addEventListener(event,()=>this.#label(),{signal});this.#sync();}
  disconnectedCallback(){super.disconnectedCallback();this.#observer?.disconnect();this.#bodyObserver?.disconnect();this.#abort?.abort();this.querySelector('video')?.pause();}
  #visible(){const drawer=this.closest('header-drawer');return this.#desktop.matches&&drawer.querySelector('.menu-drawer-container').open&&!drawer.querySelector('.menu-drawer__menu-container[open]');}
  #sync(){const v=this.querySelector('video');if(!this.#visible()){v.pause();if(!this.closest('header-drawer').querySelector('.menu-drawer-container').open)this.#manualPause=false;}else if(document.hidden||this.#motion.matches||document.body.classList.contains('rg-a11y-motion')||this.#manualPause)v.pause();else {loadCloseVideo(v);v.play().catch(()=>this.#label());}this.#label();}
  #label(){const v=this.querySelector('video'),b=this.querySelector('button'),label=v.paused?this.dataset.play:this.dataset.pause;if(b.textContent!==label)b.textContent=label;if(b.getAttribute('aria-label')!==label)b.setAttribute('aria-label',label);}
  toggle(){const v=this.querySelector('video');this.#manualPause=!v.paused;if(v.paused&&this.#visible()&&!document.hidden){loadCloseVideo(v);v.play().catch(()=>{});}else v.pause();}
}
if(!customElements.get('rg-menu-video'))customElements.define('rg-menu-video',CloseMenuVideo);
class CloseProfileGallery extends Component {
 previous(){this.#move(-1);}
 next(){this.#move(1);}
 #move(direction){const rail=this.querySelector('.rg-cs-profile-gallery__rail');rail.scrollBy({left:direction*(rail.querySelector('a')?.getBoundingClientRect().width||300),behavior:document.body.classList.contains('rg-a11y-motion')||matchMedia('(prefers-reduced-motion:reduce)').matches?'instant':'smooth'});}
}
if(!customElements.get('rg-profile-gallery'))customElements.define('rg-profile-gallery',CloseProfileGallery);

/** Contextual product shortcut: show outside the buying panel, point back toward it. */
class CloseProductJump extends Component {
 #abort; #observer; #frame; #section; #bar; #barObserver;
 connectedCallback(){super.connectedCallback();this.#section=document.getElementById(this.dataset.section);if(!this.#section)return;this.#abort=new AbortController();const signal=this.#abort.signal;const update=()=>{if(!this.#frame)this.#frame=requestAnimationFrame(()=>{this.#frame=null;this.#sync();});};window.addEventListener('scroll',update,{passive:true,signal});window.addEventListener('resize',update,{signal});this.#observer=new ResizeObserver(update);this.#observer.observe(this.#section);const watchBar=()=>{const bar=document.getElementById('PBarNextFrameWrapper');if(bar===this.#bar)return;if(this.#bar)this.#observer.unobserve(this.#bar);this.#bar=bar;if(bar)this.#observer.observe(bar);update();};this.#barObserver=new MutationObserver(watchBar);this.#barObserver.observe(document.body,{childList:true});watchBar();this.#sync();}
 disconnectedCallback(){super.disconnectedCallback();this.#abort?.abort();this.#observer?.disconnect();this.#barObserver?.disconnect();cancelAnimationFrame(this.#frame);}
 #sync(){const barBox=this.#bar?.getBoundingClientRect();const inset=barBox?.height&&barBox?.width?Math.max(0,Math.min(150,barBox.height)):0;this.style.setProperty('--rg-preview-inset',`${inset}px`);const details=this.#section.querySelector('.product-details');if(!details)return;const box=details.getBoundingClientRect(),header=document.querySelector('header-component')?.getBoundingClientRect().height||60;const above=box.top>innerHeight-110;const below=box.bottom<header+80;this.hidden=!(above||below);this.dataset.direction=below?'up':'down';}
 jump(){const details=this.#section?.querySelector('.product-details');if(!details)return;const header=document.querySelector('header-component')?.getBoundingClientRect().height||60;const reduced=document.body.classList.contains('rg-a11y-motion')||matchMedia('(prefers-reduced-motion:reduce)').matches;window.scrollTo({top:scrollY+details.getBoundingClientRect().top-header-20,behavior:reduced?'instant':'smooth'});details.setAttribute('tabindex','-1');details.focus({preventScroll:true});}
}
if(!customElements.get('rg-product-jump'))customElements.define('rg-product-jump',CloseProductJump);

/** Plays only the visible responsive banner, respecting visitor motion preferences. */
class CloseCollectionMedia extends Component {
  #abort; #observer; #bodyObserver; #visible=false; #paused=false;
  #motion=matchMedia('(prefers-reduced-motion: reduce)'); #mobile=matchMedia('(max-width:749px)');
  connectedCallback(){super.connectedCallback();this.#abort=new AbortController();const signal=this.#abort.signal;for(const video of this.querySelectorAll('video')){video.controls=false;for(const event of ['play','pause','error'])video.addEventListener(event,()=>this.#label(),{signal});}const button=this.querySelector('button');if(button)button.hidden=false;this.#observer=new IntersectionObserver(entries=>{this.#visible=entries[0].isIntersecting;this.#sync();},{threshold:0});this.#observer.observe(this);this.#bodyObserver=new MutationObserver(()=>this.#sync());this.#bodyObserver.observe(document.body,{attributes:true,attributeFilter:['class']});this.#motion.addEventListener('change',()=>this.#sync(),{signal});this.#mobile.addEventListener('change',()=>this.#sync(),{signal});document.addEventListener('visibilitychange',()=>this.#sync(),{signal});this.#label();}
  disconnectedCallback(){super.disconnectedCallback();this.#abort?.abort();this.#observer?.disconnect();this.#bodyObserver?.disconnect();for(const video of this.querySelectorAll('video'))video.pause();}
  #active(){return this.querySelector(this.#mobile.matches?'.rg-collection-media__film--mobile video':'.rg-collection-media__film--desktop video');}
  #sync(){const active=this.#active();for(const video of this.querySelectorAll('video')){if(video!==active||!this.#visible||document.hidden||this.#paused||this.#motion.matches||document.body.classList.contains('rg-a11y-motion'))video.pause();else {loadCloseVideo(video);video.play().catch(()=>this.#label());}}this.#label();}
  #label(){const video=this.#active(),button=this.querySelector('button');if(!video||!button)return;const label=video.paused?this.dataset.play:this.dataset.pause;if(button.textContent!==label)button.textContent=label;if(button.getAttribute('aria-label')!==label)button.setAttribute('aria-label',label);}
  toggle(){const video=this.#active();if(!video)return;this.#paused=!video.paused;if(video.paused){this.#paused=false;loadCloseVideo(video);video.play().catch(()=>this.#label());}else video.pause();}
}
if(!customElements.get('rg-collection-media'))customElements.define('rg-collection-media',CloseCollectionMedia);

/** Keep the menu utilities above Shopify's preview toolbar. */
const syncDrawerViewport = () => {
  const bar = document.getElementById('PBarNextFrameWrapper');
  const box = bar?.getBoundingClientRect();
  const inset = box?.width && box?.height ? Math.min(150, box.height) : 0;
  document.documentElement.style.setProperty('--rg-drawer-preview-inset', inset + 'px');
};
const drawerViewportObserver = new ResizeObserver(syncDrawerViewport);
let observedPreviewBar;
const observeDrawerViewport = () => {
  const bar = document.getElementById('PBarNextFrameWrapper');
  if (bar !== observedPreviewBar) {
    if (observedPreviewBar) drawerViewportObserver.unobserve(observedPreviewBar);
    observedPreviewBar = bar;
    if (bar) drawerViewportObserver.observe(bar);
  }
  syncDrawerViewport();
};
new MutationObserver(observeDrawerViewport).observe(document.body, { childList: true });
window.addEventListener('resize', syncDrawerViewport, { passive: true });
window.visualViewport?.addEventListener('resize', syncDrawerViewport, { passive: true });
observeDrawerViewport();

/** A lightweight moving photograph detail, paused offscreen and for reduced motion. */
class ClosePhotoPan extends HTMLElement {
  #observer; #bodyObserver; #abort; #visible = false;
  #motion = matchMedia('(prefers-reduced-motion: reduce)');
  connectedCallback() {
    this.#abort = new AbortController();
    const signal = this.#abort.signal;
    this.#observer = new IntersectionObserver(entries => { this.#visible = entries[0].isIntersecting; this.#sync(); }, { threshold: 0.15 });
    this.#observer.observe(this);
    this.#bodyObserver = new MutationObserver(() => this.#sync());
    this.#bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    document.addEventListener('visibilitychange', () => this.#sync(), { signal });
    this.#motion.addEventListener('change', () => this.#sync(), { signal });
  }
  disconnectedCallback() { this.#observer?.disconnect(); this.#bodyObserver?.disconnect(); this.#abort?.abort(); }
  #sync() { this.toggleAttribute('data-playing', this.#visible && !document.hidden && !this.#motion.matches && !document.body.classList.contains('rg-a11y-motion')); }
}
if (!customElements.get('rg-photo-pan')) customElements.define('rg-photo-pan', ClosePhotoPan);
